import { Image } from '../../../components/image';
import { Skeleton } from '../../../components/skeleton';
import { cn } from '../../../utils/cn';
import { CheckIcon } from 'lucide-react';

interface BaseDesignBoardData {
    id: number;
    title: string;
    pins?: Array<{
        id: number;
        title?: string;
        image: string;
        libraryItemId?: number;
    }>;
}

// Support both DesignBoardViewModel and GetDesignBoardListItemResponse types
export type DesignBoardData = BaseDesignBoardData & {
    urlSegment?: string;
    isNew?: boolean;
};

export interface DesignBoardTileProps {
    /** The design board data to display */
    board: DesignBoardData;
    /** Whether this tile is in a selected/linked state */
    isSelected: boolean;

    /** Whether this tile is currently loading/processing */
    isLoading?: boolean;
    /** Whether to show hover button (vs click-and-select mode) */
    showHoverButton?: boolean;
    /** Text to display on the hover button */
    buttonText?: string;
    /** Button color scheme */
    buttonColor?: 'primary' | 'secondary';
    /** Selected state color scheme */
    selectedColor?: 'primary' | 'green';
    /** Callback when tile is clicked - returns board ID for maximum reusability */
    onTileClick: (boardId: number) => void;
    /** Additional CSS classes */
    className?: string;
}

/**
 * Reusable design board tile component that can be used across different contexts.
 * Supports both hover button mode and click-and-select mode with customizable styling.
 */
export const DesignBoardTile = ({
    board,
    isSelected,
    isLoading = false,
    showHoverButton = true,
    buttonText = 'Select',
    buttonColor = 'primary',
    selectedColor = 'primary',
    onTileClick,
    className,
}: DesignBoardTileProps) => {
    const handleClick = () => {
        if (isLoading) {
            return;
        }

        onTileClick(board.id);
    };

    const getButtonColorClasses = () => {
        switch (buttonColor) {
            case 'secondary':
                return 'bg-gray-600 text-white border-gray-600 hover:bg-gray-700';
            case 'primary':
            default:
                return 'bg-brick-50 text-primary-foreground border-brick-50';
        }
    };

    const getSelectedColorClasses = () => {
        switch (selectedColor) {
            case 'green':
                return 'bg-green-600';
            case 'primary':
            default:
                return 'bg-brick-800';
        }
    };

    return (
        <div
            className={cn(
                'group relative grid aspect-[4/2.9] w-full grid-cols-1 grid-rows-3 overflow-hidden rounded-lg bg-white pb-[1.2rem] pl-[0.595rem] pr-[0.77906rem] pt-[0.64231rem] md:aspect-[4/3] md:pt-2',
                isLoading ? 'cursor-default' : 'cursor-pointer',
                className
            )}
            onClick={handleClick}
        >
            {/* Board preview images */}
            <div className="row-span-2 grid h-full grid-cols-3 grid-rows-2 gap-1">
                {[0, 1, 2].map((index) => {
                    const pin = board.pins?.[index];

                    return pin ? (
                        <div
                            key={pin.id}
                            className={cn(
                                'relative h-full overflow-hidden rounded-sm bg-gray-200',
                                index === 0
                                    ? 'col-span-2 row-span-2'
                                    : undefined
                            )}
                        >
                            <Image
                                src={pin.image}
                                alt=""
                                fill={true}
                                fit="cover"
                                multiplied={true}
                            />
                        </div>
                    ) : (
                        <div
                            key={index}
                            className={cn(
                                'relative h-full rounded-sm bg-gray-100',
                                index === 0
                                    ? 'col-span-2 row-span-2'
                                    : undefined
                            )}
                        />
                    );
                })}
            </div>

            {/* Board title */}
            <div className="h-full">
                <span
                    className={cn(
                        'mt-2 line-clamp-2 w-full text-ellipsis text-left font-medium',
                        isSelected ? 'text-gray-500' : 'text-gray-900'
                    )}
                >
                    {board.title}
                </span>
            </div>

            {/* Hover overlay with button */}
            {showHoverButton && !isSelected && !isLoading && (
                <div className="absolute inset-0 hidden items-center justify-center bg-gray-500/20 group-hover:flex">
                    <div
                        className={cn(
                            'inline-flex items-center justify-center whitespace-nowrap rounded-full border px-6 py-2 text-base font-medium transition-colors',
                            getButtonColorClasses()
                        )}
                    >
                        {buttonText}
                    </div>
                </div>
            )}

            {/* Selected state indicator */}
            {isSelected && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-500/20">
                    <div
                        className={cn(
                            'flex size-14.5 items-center justify-center rounded-full',
                            getSelectedColorClasses()
                        )}
                    >
                        <CheckIcon className="size-5.5 text-white" />
                    </div>
                </div>
            )}

            {/* Loading state indicator */}
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-500/20">
                    <div className="bg-blue-600 flex size-14.5 items-center justify-center rounded-full">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Skeleton loading state for the design board tile
 */
export const DesignBoardTileSkeleton = ({
    className,
}: {
    className?: string;
}) => (
    <div
        className={cn(
            'grid aspect-[4/2.9] w-full grid-cols-1 grid-rows-3 overflow-hidden rounded-lg bg-white p-2 md:aspect-[4/3]',
            className
        )}
    >
        <div className="row-span-2 grid h-full grid-cols-3 grid-rows-2 gap-1">
            {[0, 1, 2].map((index) => (
                <Skeleton
                    key={index}
                    className={cn(
                        'h-full',
                        index === 0 ? 'col-span-2 row-span-2' : undefined
                    )}
                />
            ))}
        </div>

        <div className="h-full">
            <Skeleton className="mt-2 h-4 w-full" />
        </div>
    </div>
);
